/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, capitalize, Checkbox, Grid, Typography } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { CriteriaOption } from 'commons/components/SearchRelated';
import { useUpdateQueryParams } from 'commons/components/SearchRelated/SearchWithCriteria/hooks';
import * as S from 'commons/components/Table/Table.styles';
import { InboundNSIntegrationStatusEnum } from 'commons/enums';
import { IInboundInvoice } from 'commons/types';
import {
  DefaultSortProps,
  RowTitleProps,
  SortOrder,
} from 'commons/types/listPageData.interface';
import { formatDate } from 'commons/utils/formatDate';
import {
  DataTable,
  ListFilterCard,
  TableActionRow,
} from 'pages/dashboard/commons/components';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { OpenBulkDialogConfig, OpenDialogConfig } from '../configs';
import { IOpenBulkDialogData, IOpenDialogData } from '../types';

import { ActionBtnMenuInboundInvoice } from './ActionBtnMenuInboundInvoice.component';
import { InboundInvoiceItemDialogPage } from './InboundInvoiceItemDialogPage.component';

const rowTitles: RowTitleProps[] = [
  { title: 'Inbound Task Number' },
  { title: 'Invoice Number' },
  { title: 'Invoice Date', sortField: 'invoice_date' },
  { title: 'Status' },
  { title: 'Receiver' },
  { title: 'Flag Reason' },
];

const defaultSort: DefaultSortProps = {
  sortField: 'invoice_date',
  sortOrder: SortOrder.DESC,
};

const Row: React.FC<{
  data: IInboundInvoice;
  index: number;
  onCheckboxClick: (inboundTaskNumber: string, invoiceNumber: string) => void;
  checked: boolean;
  handleSetInboundInvoiceItemDetail: (value: IInboundInvoice) => void;
  selectedLocation?: number;
}> = ({
  data,
  index,
  onCheckboxClick,
  checked,
  handleSetInboundInvoiceItemDetail,
  selectedLocation,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] = useState<IOpenDialogData | null>(
    null,
  );

  const handleOpenDialog = (name: string): void => {
    setOpenDialogData(OpenDialogConfig[name.toLocaleLowerCase()]);
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn style={{ width: '5%' }}>
          <Checkbox
            data-testid={`InboundInvoiceListCheckBox{${index + 1}}`}
            onClick={() =>
              onCheckboxClick(data.inbound_task_number, data.invoice_number)
            }
            checked={checked}
          />
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '15%' }}
          data-testid={`InboundInvoiceListTaskNumber${index + 1}`}
        >
          {data.inbound_task_number}
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '10%' }}
          data-testid={`InboundInvoiceListNumber${index + 1}`}
        >
          {data.invoice_number}
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '10%' }}
          data-testid={`InboundInvoiceListDate${index + 1}`}
        >
          {formatDate(data.invoice_date, 'dd/LL/yyyy')}
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '10%', textTransform: 'capitalize' }}
          data-testid={`InboundInvoiceListStatus-${index + 1}`}
        >
          {data.integration_status.replace(/-/g, ' ')}
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '20%' }}
          data-testid={`InboundInvoiceListReceiver-${index + 1}`}
        >
          {data.receiver.split('@')[0]}
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '20%' }}
          data-testid={`InboundInvoiceListFlagReason-${index + 1}`}
        >
          {data.integration_status ===
            InboundNSIntegrationStatusEnum.NS_FAILED &&
          data.related_document?.error_history
            ? data.related_document?.error_history[
                data.related_document.error_history.length - 1
              ].message
            : ''}
        </S.TableColumn>
        <S.TableColumn
          style={{ width: '10%', textAlign: 'right' }}
          data-testid={`InboundInvoiceListRowActionBtn-${index + 1}`}
        >
          {data.integration_status ===
          InboundNSIntegrationStatusEnum.NS_FAILED ? (
            <Button
              data-testid={`buttonInboundInvoiceListRetry{${index + 1}}`}
              onClick={() => handleOpenDialog('retry')}
              variant="outlined"
              sx={{
                width: 87,
              }}
            >
              Retry
            </Button>
          ) : null}
          <Button
            data-testid={`buttonInboundInvoiceListDetail-{${index + 1}}`}
            onClick={() => handleSetInboundInvoiceItemDetail(data)}
            variant="outlined"
            sx={{
              width: 87,
            }}
          >
            Details
          </Button>
        </S.TableColumn>
      </S.TableRow>
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={
          openDialogData?.content(
            data.invoice_number,
            data.inbound_task_number,
          ) || ''
        }
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            data.invoice_number,
            data.inbound_task_number,
            selectedLocation,
          )
        }
      />
    </>
  );
};

export const InboundInvoiceListTable: React.FC = (): React.ReactNode => {
  /* CONSTANTS */
  const dispatch = useDispatch<AppDispatch>();
  const { queryParams, updateQueryParams } = useUpdateQueryParams();

  const { inboundInvoiceList, selectedLocation } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const statusArrayText: {
    key: InboundNSIntegrationStatusEnum | 'all';
    text: string;
  }[] = [
    {
      key: 'all',
      text: 'All',
    },
    ...Object.values(InboundNSIntegrationStatusEnum).map((value) => ({
      key: value,
      text: capitalize(value.replace('-', ' ')),
    })),
  ];

  const searchCriteria: CriteriaOption[] = [
    {
      name: 'Inbound Task Number',
      id: 'inbound_task_numbers',
      isArray: true,
      parameterMapping: 'inbound_task_numbers',
    },
    {
      name: 'Invoice Number',
      id: 'invoice_numbers',
      isArray: true,
      parameterMapping: 'invoice_numbers',
    },
    {
      name: 'Receiver',
      id: 'receivers',
      isArray: true,
      parameterMapping: 'receivers',
    },
  ];

  /* HOOKS */
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedInvoices, setCheckedInvoices] = useState<
    { inbound_task_number: string; invoice_number: string }[]
  >([]);
  const [showDetailInboundInvoice, setShowDetailInboundInvoice] =
    useState<IInboundInvoice | null>(null);

  const [statusFilter, setStatusFilter] =
    useState<InboundNSIntegrationStatusEnum | null>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] =
    useState<IOpenBulkDialogData | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  useEffect(() => {
    updateQueryParams({}, { clearOther: true });
  }, []);

  /* FUNCTIONS */
  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (name: string): void => {
    setOpenDialogData(OpenBulkDialogConfig[name]);
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpenDialogData(null);
    setOpen(false);
  };

  const handleStatusFilter = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: InboundNSIntegrationStatusEnum,
  ): void => {
    setStatusFilter(value);
    updateQueryParams(
      { integration_status: value },
      { clearQueryParams: ['page'] },
    );
  };

  const selectedInvoices = inboundInvoiceList.filter((invoice) =>
    checkedInvoices.find(
      (checkedInvoice) =>
        checkedInvoice.inbound_task_number === invoice.inbound_task_number &&
        checkedInvoice.invoice_number === invoice.invoice_number,
    ),
  );

  const selectedInvoiceIdentifiers = selectedInvoices.map((invoice) => {
    return {
      invoice_number: invoice.invoice_number,
      inbound_task_number: invoice.inbound_task_number,
    };
  });

  const handleCheckInvoice = (
    inboundTaskNumber: string,
    invoiceNumber: string,
  ): void => {
    if (
      checkedInvoices.find(
        (checkedInvoice) =>
          checkedInvoice.inbound_task_number === inboundTaskNumber &&
          checkedInvoice.invoice_number === invoiceNumber,
      )
    ) {
      // remove invoice from checkedInvoices
      checkedInvoices.splice(
        checkedInvoices.findIndex(
          (checkedInvoice) =>
            checkedInvoice.inbound_task_number === inboundTaskNumber &&
            checkedInvoice.invoice_number === invoiceNumber,
        ),
        1,
      );
      setCheckedInvoices([...checkedInvoices]);
    } else {
      // insert invoice to checkedInvoices
      setCheckedInvoices([
        ...checkedInvoices,
        {
          inbound_task_number: inboundTaskNumber,
          invoice_number: invoiceNumber,
        },
      ]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedInvoices([]);
    } else {
      // add all invoices to checkedInvoices
      setCheckedInvoices(
        inboundInvoiceList.map((invoice) => {
          return {
            inbound_task_number: invoice.inbound_task_number,
            invoice_number: invoice.invoice_number,
          };
        }),
      );
    }
    setCheckAll(!checkAll);
  };

  const handleSetInboundInvoiceItemDetail = (
    value: IInboundInvoice | null,
  ): void => {
    setShowDetailInboundInvoice(value);
  };

  return (
    <>
      <ListFilterCard
        statusArrayText={statusArrayText}
        searchCriteria={searchCriteria}
        statusFilter={statusFilter}
        handleStatusFilter={handleStatusFilter}
      />
      <div style={{ marginTop: '24px' }}>
        {Object.keys(queryParams).includes('integration_status') ? (
          <>
            <TableActionRow
              dataName="inbound invoice"
              selectedData={selectedInvoices}
              anchorEl={anchorEl}
              openMenu={openMenu}
              handleClickMenu={handleClickMenu}
            >
              <ActionBtnMenuInboundInvoice
                selectedInvoices={selectedInvoices}
                handleCloseMenu={handleCloseMenu}
                handleOpen={handleOpenDialog}
              />
            </TableActionRow>
            <DataTable
              rowTitles={rowTitles}
              checkAll={checkAll}
              handleCheckAll={handleCheckAll}
              defaultSort={defaultSort}
            >
              <S.TableBody>
                {inboundInvoiceList.map((invoice, index) => (
                  <Row
                    data={invoice}
                    index={index}
                    onCheckboxClick={handleCheckInvoice}
                    checked={checkedInvoices.some(
                      (checkedInvoice) =>
                        checkedInvoice.inbound_task_number ===
                          invoice.inbound_task_number &&
                        checkedInvoice.invoice_number ===
                          invoice.invoice_number,
                    )}
                    handleSetInboundInvoiceItemDetail={
                      handleSetInboundInvoiceItemDetail
                    }
                    selectedLocation={selectedLocation?.warehouse_id}
                  />
                ))}
              </S.TableBody>
            </DataTable>
            {inboundInvoiceList.length > 0 && (
              <TableActionRow
                dataName="inbound invoice"
                selectedData={selectedInvoices}
                anchorEl={anchorEl}
                openMenu={openMenu}
                handleClickMenu={handleClickMenu}
              >
                <ActionBtnMenuInboundInvoice
                  selectedInvoices={selectedInvoices}
                  handleCloseMenu={handleCloseMenu}
                  handleOpen={handleOpenDialog}
                />
              </TableActionRow>
            )}
            <InboundInvoiceItemDialogPage
              inboundInvoice={showDetailInboundInvoice}
              onClose={() => handleSetInboundInvoiceItemDetail(null)}
            />
          </>
        ) : (
          <Grid container justifyContent="center">
            <Typography>Please select a status</Typography>
          </Grid>
        )}
      </div>
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(selectedInvoiceIdentifiers) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            selectedInvoiceIdentifiers,
            selectedLocation?.warehouse_id,
          )
        }
      />
    </>
  );
};
